import type {
  PaletteColor,
  PaletteMode,
  PaletteOptions,
  Shadows,
  ThemeOptions,
} from '@mui/material/styles'
import { createTheme, darken } from '@mui/material/styles'

import {
  buildReadableTextColor,
  ensureContrast,
  generateAdvancedStatesFromHex,
  getBestContrastColor,
  withAlpha,
} from '../../../../utils/getColor.ts'
import type { CustomColor } from './theme'
import { themeDarkShadow, themeLightShadow } from './themeShadows.ts'

type BuiltPaletteColor = PaletteColor & { base: string }

export { getBestContrastColor } from '../../../../utils/getColor.ts'
export const themeShadows: Shadows = themeLightShadow

const getDefaultBg = (isDark: boolean): string => (isDark ? '#111111' : '#F4F5F7')
const getPaperBg = (isDark: boolean): string => (isDark ? '#1B1B1B' : '#FFFFFF')
const getElevatedBg = (isDark: boolean): string => (isDark ? '#262626' : '#FFFFFF')

const textLight = {
  primary: 'rgba(52, 49, 76, 1)',
  secondary: 'rgba(52, 49, 76, 0.70)',
  disabled: 'rgba(52, 49, 76, 0.38)',
  hint: 'rgba(52, 49, 76, 0.60)',
}

const textDark = {
  primary: '#C5C7CA', // Suave, blanco tiza mate (~78% brillo, sin fatiga)
  secondary: '#8E949E', // 58% brillo (crea jerarquía clara y descansa la vista)
  disabled: 'rgba(255, 255, 255, 0.36)',
  hint: 'rgba(255, 255, 255, 0.48)',
}

const extendedDefinitions = {
  yellow: { base: '#FBC02D', darkMain: '#C49B36' },
  green: { base: '#4CAF50', darkMain: '#4E9B5A' },
  blue: { base: '#2196F3', darkMain: '#4E92D0' },
  cyan: { base: '#00BCD4', darkMain: '#33A6B8' },
  purple: { base: '#9C27B0', darkMain: '#9B5AA8' },
  teal: { base: '#009688', darkMain: '#3FA599' },
  orange: { base: '#F57C00', darkMain: '#C46E18' },
} as const

// createTheme() sin argumentos permite obtener el generador estándar de MUI.
// Los colores iniciales satisfacen también proyectos con PaletteOptions obligatorio.
const initialPalette = createTheme().palette
const initialColor = (name: CustomColor): PaletteColor =>
  initialPalette.augmentColor({
    name,
    color: { main: extendedDefinitions[name].base },
  })

const extendedDefaults = {
  yellow: initialColor('yellow'),
  green: initialColor('green'),
  blue: initialColor('blue'),
  cyan: initialColor('cyan'),
  purple: initialColor('purple'),
  teal: initialColor('teal'),
  orange: initialColor('orange'),
}

// Configura las reglas de augmentColor. Los colores finales se construyen después.
const paletteBuilders = {
  light: createTheme({
    palette: {
      ...extendedDefaults,
      mode: 'light',
      contrastThreshold: 4.5,
      tonalOffset: { light: 0.12, dark: 0.12 },
    },
  }).palette,
  dark: createTheme({
    palette: {
      ...extendedDefaults,
      mode: 'dark',
      contrastThreshold: 4.5,
      tonalOffset: { light: 0.08, dark: 0.12 },
    },
  }).palette,
}

// main es una decisión explícita de diseño; no se desatura ni se aclara aquí.
const buildColorFromBase = (
  base: string,
  isDark: boolean,
  darkMain: string,
): BuiltPaletteColor => {
  const paper = getPaperBg(isDark)
  const main = isDark ? darkMain : base
  const contrastText = getBestContrastColor(main)
  const generated = paletteBuilders[isDark ? 'dark' : 'light'].augmentColor({
    color: { main, contrastText },
  })
  // Los botones rellenos tienen fondos propios; main sigue disponible para texto.
  const buttonTextColor = isDark ? '#E8EAED' : '#FFFFFF'
  // Evita oscurecer un tono pastel de texto: parte del color original de empresa.
  const buttonSeed = isDark ? base : main
  const buttonBgColor = ensureContrast(buttonSeed, buttonTextColor, false, 4.5)
  const buttonHoverBgColor = darken(buttonBgColor, 0.1)
  // Checkbox, Radio, Switch y Slider requieren contraste gráfico, no brillo de texto.
  const controlColor = ensureContrast(
    isDark ? buttonBgColor : base,
    getElevatedBg(isDark),
    isDark,
    3,
  )
  // Suaviza solo el texto en oscuro; main conserva los colores de botones e iconos.
  const mainTextColor = isDark
    ? buildReadableTextColor(
        darken(main, 0.06),
        [paper, getDefaultBg(isDark), getElevatedBg(isDark)],
        true,
      )
    : main
  return {
    ...generated,
    base,
    buttonBgColor,
    buttonHoverBgColor,
    buttonTextColor,
    controlColor,
    mainTextColor,
    // Mantiene legible la etiqueta también cuando el botón usa dark en hover.
    dark: ensureContrast(generated.dark, contrastText, contrastText === '#000000', 4.5),
    textColor: buildReadableTextColor(
      main,
      [paper, getDefaultBg(isDark), getElevatedBg(isDark)],
      isDark,
    ),
    ...generateAdvancedStatesFromHex(main, paper, isDark),
  }
}

const semanticDefinitions = {
  error: { base: '#d32f2f', darkMain: '#B85252' },
  warning: { base: '#ed6c02', darkMain: '#BA621E' },
  success: { base: '#2e7d32', darkMain: '#4E9B5A' },
  info: { base: '#0288d1', darkMain: '#3B98C6' },
} as const

const colors = {
  default: { pri: '#363e5d', sec: '#df9c16' },
  green: { pri: '#00625D', sec: '#F47A20' },
  indigo: { pri: '#1c4c96', sec: '#ff7360' },
  purple: { pri: '#4745b6', sec: '#ff8000' },
  blue: { pri: '#1976d2', sec: '#d27619' },
  blue1: { pri: '#00539A', sec: '#E15200' },
  blue2: { pri: '#25368B', sec: '#FCC346' },
  purple2: { pri: '#584569', sec: '#ED6C20' },
} as const

export type ThemeVariant = keyof typeof colors
export type ThemeName = ThemeVariant | `${ThemeVariant}Dark`

// Adaptaciones de marca para oscuro: decisiones editables, no colores oficiales de MUI.
// Los hex originales de empresa permanecen en colors y en el token base.
const darkColors = {
  default: { pri: '#7485B8', sec: '#D89828' },
  green: { pri: '#3CA095', sec: '#D77C38' },
  indigo: { pri: '#5D8ED4', sec: '#E06B5B' },
  purple: { pri: '#7F7CD6', sec: '#D97716' },
  blue: { pri: '#5296D5', sec: '#BF762A' },
  blue1: { pri: '#4F97CE', sec: '#CC743B' },
  blue2: { pri: '#7083CC', sec: '#E5AF36' },
  purple2: { pri: '#9B80AB', sec: '#D27138' },
} satisfies Record<ThemeVariant, { pri: string; sec: string }>

const buildPaletteGroup = <K extends string>(
  definitions: Record<K, { base: string; darkMain: string }>,
  isDark: boolean,
): Record<K, PaletteColor> => {
  // Object.entries pierde las claves literales; la aserción se limita a este constructor.
  const result = {} as Record<K, PaletteColor>
  for (const key of Object.keys(definitions) as K[]) {
    const { base, darkMain } = definitions[key]
    result[key] = buildColorFromBase(base, isDark, darkMain)
  }
  return result
}

// Estos colores dependen del modo, no de la variante de marca.
const sharedColors = {
  light: {
    ...buildPaletteGroup(extendedDefinitions, false),
    ...buildPaletteGroup(semanticDefinitions, false),
  },
  dark: {
    ...buildPaletteGroup(extendedDefinitions, true),
    ...buildPaletteGroup(semanticDefinitions, true),
  },
}

// Conserva tu contenedor, compartiéndolo entre variantes y creando el nodo bajo demanda.
const getModalContainer = (): HTMLElement | null => {
  if (typeof document === 'undefined') return null
  const existing = document.getElementById('modal-root')
  if (existing) return existing
  const container = document.createElement('div')
  container.id = 'modal-root'
  document.body.appendChild(container)
  return container
}

const createCompleteTheme = (variant: ThemeVariant, mode: PaletteMode): ThemeOptions => {
  const isDark = mode === 'dark'
  const neutral = isDark ? '#ffffff' : '#000000'
  const palette = {
    mode,
    contrastThreshold: 4.5,
    ...sharedColors[mode],
    primary: buildColorFromBase(colors[variant].pri, isDark, darkColors[variant].pri),
    secondary: buildColorFromBase(colors[variant].sec, isDark, darkColors[variant].sec),
    text: isDark ? textDark : textLight,
    divider: withAlpha(neutral, 0.12),
    background: {
      paper: getPaperBg(isDark),
      default: getDefaultBg(isDark),
      elevated: getElevatedBg(isDark),

      // Input editable: sutilmente elevado sobre el paper para delimitar el área interactiva
      input: isDark ? 'rgba(255, 255, 255, 0.045)' : 'rgba(0, 0, 0, 0.02)',
      // Input deshabilitado: se apaga y retrocede visualmente (no compite con los activos)
      inputDisabled: isDark ? 'rgba(0, 0, 0, 0.20)' : 'rgba(0, 0, 0, 0.04)',
      // Input solo lectura: integrado como texto plano sobre la superficie
      inputReadOnly: 'transparent',
    },
    action: {
      active: withAlpha(neutral, isDark ? 0.78 : 0.54),
      hover: withAlpha(neutral, isDark ? 0.08 : 0.04),
      hoverOpacity: isDark ? 0.08 : 0.04,
      selected: withAlpha(neutral, isDark ? 0.16 : 0.08),
      selectedOpacity: isDark ? 0.16 : 0.08,
      disabled: withAlpha(neutral, isDark ? 0.3 : 0.26),
      disabledBackground: withAlpha(neutral, 0.12),
      disabledOpacity: 0.38,
      focus: withAlpha(neutral, 0.12),
      focusOpacity: 0.12,
      activatedOpacity: 0.12,
    },
  } satisfies PaletteOptions

  return {
    palette,
    shadows: isDark ? themeDarkShadow : themeShadows,
    components: {
      MuiModal: { defaultProps: { container: getModalContainer } },
      MuiPopover: { defaultProps: { container: getModalContainer } },
    },
  }
}

export const themeColors: Record<ThemeName, ThemeOptions> = (() => {
  const result = {} as Record<ThemeName, ThemeOptions>
  for (const variant of Object.keys(colors) as ThemeVariant[]) {
    result[variant] = createCompleteTheme(variant, 'light')
    result[`${variant}Dark`] = createCompleteTheme(variant, 'dark')
  }
  return result
})()
